import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataCache } from '@/hooks/useDataCache';
import {
  Plus,
  Receipt,
  Send,
  Ban,
  DollarSign,
  Loader2,
  Search,
  Filter,
  Printer,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Zap,
  AlertTriangle,
  Clock,
  Tag,
  Receipt as ReceiptIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import PaymentModal from './PaymentModal';
import IssuePpdDialog from '@/components/cfdi/IssuePpdDialog';
import { formatLocalDate } from '@/lib/utils';

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc?: string;
}

async function fetchEmpresas(): Promise<Empresa[]> {
  const res = await fetch('/api/finance/emisores', { credentials: 'include' });
  if (!res.ok) return [];
  return res.json();
}

interface InvoiceItem {
  id: number;
  productId: number;
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate?: string;
  discountAmount?: string;
  discountPct?: string;
  subtotal?: string;
  taxAmount?: string;
  total?: string;
  product?: {
    id: number;
    name: string;
    allowedPaymentMethods?: string;
  };
}

interface PricingInfo {
  scenario: 'pronto' | 'normal' | 'mora';
  effectiveBalance: string;
  discountWithTax: string;
  surchargeWithTax: string;
  dueDate: string;
  graceDeadline: string;
  installmentNumber: number;
}

interface Invoice {
  id: number;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  status: 'DRAFT' | 'ISSUED' | 'PAID' | 'PARTIALLY_PAID' | 'CANCELLED';
  subtotal: string;
  taxTotal: string;
  total: string;
  totalPaid: string;
  pendingAmount: string;
  lastPaymentDate?: string | null;
  pricingInfo?: PricingInfo | null;
  hasCfdi: boolean;
  cfdiMetodoPago: string | null;
  cfdiSaldoInsoluto: string | null;
  replacedByInvoiceId: number | null;
  replacedByInvoiceNumber: string | null;
  customer: {
    id: number;
    nui?: string;
    numeroIdentificacionUnico?: string;
    nombre: string;
    apellidoPaterno: string;
    apellidoMaterno?: string;
    group?: {
      id: number;
      name: string;
      code: string;
    };
  };
  items?: InvoiceItem[];
}

interface InvoiceListResponse {
  data: Invoice[];
  total: number;
}

interface FetchParams {
  status?: string;
  empresaId?: number | null;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

async function fetchInvoices(params: FetchParams): Promise<InvoiceListResponse> {
  const p = new URLSearchParams();
  if (params.status && params.status !== 'all') p.append('status', params.status);
  if (params.empresaId != null) p.append('empresaId', String(params.empresaId));
  if (params.search) p.append('search', params.search);
  if (params.sortBy) p.append('sortBy', params.sortBy);
  if (params.sortDir) p.append('sortDir', params.sortDir);
  p.append('limit', String(params.pageSize));
  p.append('offset', String((params.page - 1) * params.pageSize));

  const response = await fetch(`/api/sales/invoices?${p.toString()}`, { credentials: 'include' });
  if (!response.ok) throw new Error('Error al cargar facturas');
  return response.json();
}

async function issueInvoice(invoiceId: number) {
  const response = await fetch(`/api/sales/invoices/${invoiceId}/issue`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al emitir factura');
  }
  return response.json();
}

async function cancelInvoice(invoiceId: number, linkToInvoiceId?: number) {
  const response = await fetch(`/api/sales/invoices/${invoiceId}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linkToInvoiceId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error al cancelar factura');
  }
  return response.json();
}

const statusLabels: Record<string, string> = {
  DRAFT: 'Borrador',
  ISSUED: 'Emitida',
  PAID: 'Pagada',
  PARTIALLY_PAID: 'Pago Parcial',
  CANCELLED: 'Cancelada',
};

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
  PARTIALLY_PAID: 'bg-yellow-100 text-yellow-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

type SortKey = 'invoiceNumber' | 'customer' | 'date' | 'dueDate' | 'total' | 'status';

function SortIcon({ colKey, sortBy, sortDir }: { colKey: SortKey; sortBy: SortKey; sortDir: 'asc' | 'desc' }) {
  if (sortBy !== colKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40 inline" />;
  return sortDir === 'asc'
    ? <ArrowUp className="w-3 h-3 ml-1 text-emerald-600 inline" />
    : <ArrowDown className="w-3 h-3 ml-1 text-emerald-600 inline" />;
}

const PAGE_SIZES = [25, 50, 100, 200];

export default function InvoicesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { cachedData } = useDataCache();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [ppdDialogInvoice, setPpdDialogInvoice] = useState<Invoice | null>(null);
  const [cancelDialogInvoice, setCancelDialogInvoice] = useState<Invoice | null>(null);
  const [cancelLinkToId, setCancelLinkToId] = useState<number | null>(null);

  const empresaParam = searchParams.get('empresa');
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(
    empresaParam ? parseInt(empresaParam) : null
  );

  useEffect(() => {
    const p = searchParams.get('empresa');
    setSelectedEmpresaId(p ? parseInt(p) : null);
  }, [empresaParam]);

  const handleSetEmpresa = (id: number | null) => {
    setSelectedEmpresaId(id);
    setPage(1);
    if (id !== null) {
      setSearchParams({ empresa: String(id) });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSort = useCallback((key: SortKey) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
    setPage(1);
  }, [sortBy]);

  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const topInnerRef = useRef<HTMLDivElement>(null);

  const { data: empresas = [] } = useQuery({
    queryKey: ['finance-emisores'],
    queryFn: fetchEmpresas,
  });

  const { data: linkableInvoices = [] } = useQuery({
    queryKey: ['invoices-linkable', cancelDialogInvoice?.customer.id, cancelDialogInvoice?.id],
    queryFn: async () => {
      const res = await fetch(
        `/api/sales/invoices?customerId=${cancelDialogInvoice!.customer.id}&status=ISSUED&limit=100`,
        { credentials: 'include' }
      );
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data || []).filter((inv: Invoice) => inv.id !== cancelDialogInvoice!.id);
    },
    enabled: !!cancelDialogInvoice,
  });

  const fetchParams: FetchParams = {
    status: statusFilter,
    empresaId: selectedEmpresaId,
    search: debouncedSearch || undefined,
    sortBy,
    sortDir,
    page,
    pageSize,
  };

  const { data: result, isLoading, isFetching } = useQuery({
    queryKey: ['sales-invoices', fetchParams],
    queryFn: () => fetchInvoices(fetchParams),
    placeholderData: (prev) => prev,
  });

  const invoiceList: Invoice[] = result?.data ?? [];
  const total: number = result?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    const tableEl = tableScrollRef.current?.querySelector('table');
    if (!tableEl || !topInnerRef.current) return;
    const syncWidth = () => {
      const table = tableScrollRef.current?.querySelector('table');
      if (table && topInnerRef.current) topInnerRef.current.style.width = `${table.scrollWidth}px`;
    };
    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(tableEl);
    return () => observer.disconnect();
  }, [invoiceList]);

  const handleTopScroll = () => {
    if (tableScrollRef.current && topScrollRef.current)
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };
  const handleTableScroll = () => {
    if (topScrollRef.current && tableScrollRef.current)
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
  };

  const issueMutation = useMutation({
    mutationFn: issueInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      toast({ title: 'Factura emitida', description: 'La factura ha sido emitida y se ha generado la póliza contable.' });
    },
    onError: (error: Error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ invoiceId, linkToInvoiceId }: { invoiceId: number; linkToInvoiceId?: number }) =>
      cancelInvoice(invoiceId, linkToInvoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      setCancelDialogInvoice(null);
      setCancelLinkToId(null);
      toast({
        title: 'Factura cancelada',
        description: cancelLinkToId
          ? 'Se canceló y quedó vinculada al recibo sustituto.'
          : 'La factura ha sido cancelada.',
      });
    },
    onError: (error: Error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const handleOpenPaymentModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setIsPaymentModalOpen(false);
    setSelectedInvoice(null);
    queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
  };

  const formatDate = (dateStr: string) =>
    formatLocalDate(dateStr, { year: 'numeric', month: 'short', day: 'numeric' });

  const formatCurrency = (amount: string) =>
    parseFloat(amount).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

  const canPay = (status: string) => status === 'ISSUED' || status === 'PARTIALLY_PAID';
  const canIssue = (status: string) => status === 'DRAFT';
  const canCancel = (status: string) => status === 'DRAFT' || status === 'ISSUED';
  const canIssuePpd = (invoice: Invoice) =>
    !invoice.hasCfdi && ['ISSUED', 'PARTIALLY_PAID', 'PAID'].includes(invoice.status);

  const printInvoice = (invoice: Invoice) => {
    const customerName = `${invoice.customer?.apellidoPaterno || ''} ${invoice.customer?.apellidoMaterno || ''} ${invoice.customer?.nombre || ''}`.replace(/\s+/g, ' ').trim();
    const nui = invoice.customer?.nui || invoice.customer?.numeroIdentificacionUnico || '-';
    const subtotal = parseFloat(invoice.subtotal || '0');
    const taxTotal = parseFloat(invoice.taxTotal || '0');
    const total = parseFloat(invoice.total || '0');
    const totalPaid = parseFloat(invoice.totalPaid || '0');
    const pending = parseFloat(invoice.pendingAmount || '0');
    const statusLabel = statusLabels[invoice.status] || invoice.status;
    const statusColor = invoice.status === 'PAID' ? '#166534' : invoice.status === 'ISSUED' ? '#1d4ed8' : invoice.status === 'PARTIALLY_PAID' ? '#854d0e' : invoice.status === 'CANCELLED' ? '#991b1b' : '#374151';
    const statusBg = invoice.status === 'PAID' ? '#dcfce7' : invoice.status === 'ISSUED' ? '#dbeafe' : invoice.status === 'PARTIALLY_PAID' ? '#fef9c3' : invoice.status === 'CANCELLED' ? '#fee2e2' : '#f3f4f6';
    const companyName = cachedData.company?.legalName || 'GQB';
    const logoUrl = cachedData.company?.logoUrl ? `${window.location.origin}${cachedData.company.logoUrl}` : null;
    const printDate = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    const fmt = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2 });

    // Compute per-item discount totals from actual item data
    const itemDiscountTotal = (invoice.items || []).reduce((sum, item) => {
      return sum + parseFloat(item.discountAmount || '0');
    }, 0);
    const hasItemDiscounts = itemDiscountTotal > 0.001;

    // Build item rows — show Descuento column only when at least one item has a discount
    const itemRows = (invoice.items || []).map(item => {
      const name = item.description || item.product?.name || '-';
      const qty = parseFloat(item.quantity || '1');
      const price = parseFloat(item.unitPrice || '0');
      const discAmt = parseFloat(item.discountAmount || '0');
      // Use stored subtotal when available, otherwise compute
      const lineSubtotal = item.subtotal ? parseFloat(item.subtotal) : qty * price - discAmt;
      const lineTax = item.taxAmount ? parseFloat(item.taxAmount) : 0;
      const lineTotal = lineSubtotal + lineTax;
      const discountCell = hasItemDiscounts
        ? `<td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;color:${discAmt > 0 ? '#059669' : '#9ca3af'};">${discAmt > 0 ? `-$${fmt(discAmt)}` : '—'}</td>`
        : '';
      return `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${name}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${qty}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">$${fmt(price)}</td>
        ${discountCell}
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">$${fmt(lineTotal)}</td>
      </tr>`;
    }).join('');

    const prontoPagoDiscount = (invoice.status === 'PAID' && totalPaid < total - 0.01) ? total - totalPaid : 0;
    const surcharge = (invoice.status === 'PAID' && totalPaid > total + 0.01) ? totalPaid - total : 0;

    // Gross amount before item-level discounts
    const grossAmount = (invoice.items || []).reduce((sum, item) => {
      return sum + parseFloat(item.quantity || '1') * parseFloat(item.unitPrice || '0');
    }, 0);

    const totalsRows = (() => {
      let rows = '';
      if (hasItemDiscounts) {
        rows += `
        <tr><td>Subtotal bruto:</td><td style="text-align:right;">$${fmt(grossAmount)}</td></tr>
        <tr class="discount-row"><td>Descuentos:</td><td style="text-align:right;">-$${fmt(itemDiscountTotal)}</td></tr>
        <tr><td>Subtotal neto:</td><td style="text-align:right;">$${fmt(subtotal)}</td></tr>`;
      } else {
        rows += `<tr><td>Subtotal:</td><td style="text-align:right;">$${fmt(subtotal)}</td></tr>`;
      }
      rows += `<tr><td>IVA:</td><td style="text-align:right;">$${fmt(taxTotal)}</td></tr>`;
      if (prontoPagoDiscount > 0.01) {
        rows += `
        <tr class="invoice-total"><td>Total factura:</td><td style="text-align:right;">$${fmt(total)}</td></tr>
        <tr class="discount-row"><td>🟢 Descuento Pronto Pago:</td><td style="text-align:right;">-$${fmt(prontoPagoDiscount)}</td></tr>
        <tr class="grand"><td>Total pagado:</td><td style="text-align:right;">$${fmt(totalPaid)}</td></tr>`;
      } else if (surcharge > 0.01) {
        rows += `
        <tr class="invoice-total"><td>Total factura:</td><td style="text-align:right;">$${fmt(total)}</td></tr>
        <tr class="surcharge-row"><td>🔴 Recargo por Mora:</td><td style="text-align:right;">+$${fmt(surcharge)}</td></tr>
        <tr class="grand"><td>Total cobrado:</td><td style="text-align:right;">$${fmt(totalPaid)}</td></tr>`;
      } else {
        rows += `
        <tr class="grand"><td>Total:</td><td style="text-align:right;">$${fmt(total)}</td></tr>
        ${totalPaid > 0 ? `<tr class="paid-row"><td>Pagado:</td><td style="text-align:right;">$${fmt(totalPaid)}</td></tr>` : ''}
        ${pending > 0 ? `<tr class="pending-row"><td>Saldo pendiente:</td><td style="text-align:right;">$${fmt(pending)}</td></tr>` : ''}`;
      }
      return rows;
    })();

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Recibo ${invoice.invoiceNumber}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; padding: 28px; color: #111; font-size: 14px; }
    .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
    .company-block { display: flex; align-items: center; gap: 12px; }
    .company-logo { max-height: 60px; max-width: 160px; object-fit: contain; }
    .company-name { font-size: 18px; font-weight: bold; color: #111; }
    .print-date { font-size: 12px; color: #6b7280; text-align: right; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-top: 2px solid #111; border-bottom: 2px solid #111; padding: 12px 0; margin-bottom: 18px; }
    .title { font-size: 20px; font-weight: bold; margin-bottom: 4px; }
    .folio { color: #6b7280; font-size: 13px; margin-bottom: 6px; }
    .badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; background: ${statusBg}; color: ${statusColor}; }
    .meta { text-align: right; font-size: 13px; color: #374151; line-height: 2; }
    .meta strong { color: #111; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 18px; }
    .info-block { background: #f9fafb; padding: 10px 12px; border-radius: 6px; }
    .info-block .label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px; }
    .info-block .value { font-weight: 600; font-size: 14px; }
    .info-block .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 16px; }
    thead th { background: #f3f4f6; padding: 8px; font-size: 11px; color: #374151; text-transform: uppercase; }
    thead th:not(:first-child) { text-align: right; }
    .totals-table { width: 300px; margin-left: auto; border-collapse: collapse; font-size: 13px; }
    .totals-table td { padding: 4px 8px; }
    .totals-table .invoice-total td { color: #374151; border-top: 1px solid #e5e7eb; padding-top: 6px; }
    .totals-table .discount-row td { color: #166534; font-weight: 600; background: #f0fdf4; }
    .totals-table .surcharge-row td { color: #991b1b; font-weight: 600; background: #fef2f2; }
    .totals-table .grand td { font-weight: bold; font-size: 16px; border-top: 2px solid #111; padding-top: 8px; }
    .totals-table .paid-row td { color: #166534; }
    .totals-table .pending-row td { color: #d97706; font-weight: 600; }
    .footer { border-top: 1px solid #e5e7eb; padding-top: 10px; font-size: 11px; color: #6b7280; text-align: center; }
    @media print { body { padding: 14px; } button { display: none; } }
  </style>
</head>
<body>
  <div class="top-bar">
    <div class="company-block">
      ${logoUrl ? `<img src="${logoUrl}" class="company-logo" alt="Logo"/>` : ''}
      <span class="company-name">${companyName}</span>
    </div>
    <div class="print-date">Impreso: ${printDate}</div>
  </div>
  <div class="header">
    <div>
      <div class="title">Cuenta de Cobro</div>
      <div class="folio">No. ${invoice.invoiceNumber}</div>
      <span class="badge">${statusLabel}</span>
    </div>
    <div class="meta">
      <div><strong>Fecha:</strong> ${formatDate(invoice.date)}</div>
      <div><strong>Vencimiento:</strong> ${formatDate(invoice.dueDate)}</div>
      ${invoice.lastPaymentDate ? `<div><strong>Fecha de Pago:</strong> ${formatDate(invoice.lastPaymentDate)}</div>` : ''}
    </div>
  </div>
  <div class="info-grid">
    <div class="info-block">
      <div class="label">Cliente</div>
      <div class="value">${customerName}</div>
      <div class="sub">NUI: ${nui}</div>
      ${invoice.customer?.group ? `<div class="sub">Grupo: ${invoice.customer.group.name}</div>` : ''}
    </div>
    <div class="info-block">
      <div class="label">Empresa Emisora</div>
      <div class="value">${companyName}</div>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th style="text-align:left;">Concepto</th>
        <th>Cant.</th>
        <th>Precio Unit.</th>
        ${hasItemDiscounts ? '<th>Descuento</th>' : ''}
        <th>Total</th>
      </tr>
    </thead>
    <tbody>${itemRows || '<tr><td colspan="4" style="padding:8px;color:#6b7280;">Sin conceptos</td></tr>'}</tbody>
  </table>
  <table class="totals-table">
    <tbody>${totalsRows}</tbody>
  </table>
  <div class="footer">Documento generado por ${companyName} · ${printDate}</div>
  <script>window.onload=()=>window.print();</script>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const thClass = (key: SortKey) =>
    `py-3 px-4 font-medium cursor-pointer select-none hover:bg-gray-100 transition-colors whitespace-nowrap ${sortBy === key ? 'text-emerald-700' : ''}`;

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cuentas de Cobro</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de facturas y cuentas por cobrar</p>
        </div>
        <Button onClick={() => navigate('/app/sales/invoices/new')} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Recibo
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border mb-4">
        {/* Search + Status filter */}
        <div className="p-4 border-b flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar en todos los registros: número, cliente, NUI..."
              className="pl-10"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {isFetching && debouncedSearch && (
              <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 animate-spin" />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="DRAFT">Borrador</SelectItem>
                <SelectItem value="ISSUED">Emitida</SelectItem>
                <SelectItem value="PARTIALLY_PAID">Pago Parcial</SelectItem>
                <SelectItem value="PAID">Pagada</SelectItem>
                <SelectItem value="CANCELLED">Cancelada</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map(s => (
                  <SelectItem key={s} value={String(s)}>{s} / pág</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Empresa pills */}
        {empresas.length > 0 && (
          <div className="px-4 py-2 border-b flex items-center gap-2 flex-wrap bg-gray-50">
            <span className="text-xs font-medium text-gray-500 shrink-0">Razón Social:</span>
            <button
              onClick={() => handleSetEmpresa(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedEmpresaId === null ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Todas
            </button>
            {empresas.map((emp) => (
              <button
                key={emp.id}
                onClick={() => handleSetEmpresa(emp.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedEmpresaId === emp.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {emp.profileName || emp.legalName}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Cargando facturas...</p>
          </div>
        ) : invoiceList.length === 0 ? (
          <div className="p-8 text-center">
            <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">
              {debouncedSearch ? `Sin resultados para "${debouncedSearch}"` : 'No hay facturas registradas'}
            </p>
            {!debouncedSearch && (
              <Button variant="outline" className="mt-4" onClick={() => navigate('/app/sales/invoices/new')}>
                Crear primera factura
              </Button>
            )}
          </div>
        ) : (
          <>
            <div
              ref={topScrollRef}
              className="overflow-x-scroll overflow-y-hidden"
              style={{ height: '14px' }}
              onScroll={handleTopScroll}
            >
              <div ref={topInnerRef} style={{ height: '1px' }} />
            </div>
            <div ref={tableScrollRef} className={`overflow-x-auto transition-opacity ${isFetching ? 'opacity-60' : ''}`} onScroll={handleTableScroll}>
              <table className="w-full">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500 border-b">
                  <tr>
                    <th className="py-3 px-4 font-medium text-center whitespace-nowrap">Acciones</th>
                    <th className={thClass('invoiceNumber')} onClick={() => handleSort('invoiceNumber')}>
                      Número <SortIcon colKey="invoiceNumber" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th className={thClass('customer')} onClick={() => handleSort('customer')}>
                      Cliente <SortIcon colKey="customer" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th className="py-3 px-4 font-medium text-left whitespace-nowrap">Grupo</th>
                    <th className="py-3 px-4 font-medium text-left whitespace-nowrap">Concepto(s)</th>
                    <th className={thClass('date')} onClick={() => handleSort('date')}>
                      Fecha <SortIcon colKey="date" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th className={thClass('dueDate')} onClick={() => handleSort('dueDate')}>
                      Vencimiento <SortIcon colKey="dueDate" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th className={`${thClass('total')} text-right`} onClick={() => handleSort('total')}>
                      Total <SortIcon colKey="total" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                    <th className="py-3 px-4 font-medium text-right whitespace-nowrap">Pagado</th>
                    <th className="py-3 px-4 font-medium text-left whitespace-nowrap">Fecha de Pago</th>
                    <th className="py-3 px-4 font-medium text-right whitespace-nowrap">Saldo</th>
                    <th className={thClass('status')} onClick={() => handleSort('status')}>
                      Estado <SortIcon colKey="status" sortBy={sortBy} sortDir={sortDir} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceList.map((invoice) => (
                    <tr key={invoice.id} className="border-t hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          {canIssue(invoice.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => issueMutation.mutate(invoice.id)}
                              disabled={issueMutation.isPending}
                              title="Emitir factura"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {canPay(invoice.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleOpenPaymentModal(invoice)}
                              title="Registrar pago"
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                            onClick={() => printInvoice(invoice)}
                            title="Imprimir ticket"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          {canIssuePpd(invoice) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              onClick={() => setPpdDialogInvoice(invoice)}
                              title="Facturar PPD (CFDI por el monto total, independiente del cobro)"
                            >
                              <ReceiptIcon className="w-4 h-4" />
                            </Button>
                          )}
                          {canCancel(invoice.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setCancelDialogInvoice(invoice)}
                              title="Cancelar factura"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm font-medium text-gray-900">{invoice.invoiceNumber}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900 whitespace-nowrap">
                            {invoice.customer?.apellidoPaterno} {invoice.customer?.apellidoMaterno || ''} {invoice.customer?.nombre}
                          </p>
                          <p className="text-xs text-gray-500">
                            NUI: {invoice.customer?.nui || invoice.customer?.numeroIdentificacionUnico || '-'}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-gray-700 whitespace-nowrap">{invoice.customer?.group?.name || '-'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="max-w-[220px]">
                          {invoice.items && invoice.items.length > 0 ? (
                            <div className="text-sm text-gray-700">
                              {invoice.items.slice(0, 2).map((item, idx) => {
                                const itemDiscount = parseFloat(item.discountAmount || '0');
                                const promoDiscount = parseFloat((item as any).promotionDiscountAmount || '0');
                                return (
                                  <div key={idx} className="flex items-center gap-1 min-w-0">
                                    <span className="truncate" title={item.description || item.product?.name || '-'}>
                                      {item.description || item.product?.name || '-'}
                                    </span>
                                    {itemDiscount > 0 && (
                                      <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5 whitespace-nowrap">
                                        <Tag className="w-2.5 h-2.5" />
                                        -{formatCurrency(item.discountAmount!)}
                                      </span>
                                    )}
                                    {promoDiscount > 0 && (
                                      <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 py-0.5 whitespace-nowrap">
                                        ✦ -{formatCurrency(String(promoDiscount))}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              {invoice.items.length > 2 && (
                                <p className="text-xs text-gray-500">+{invoice.items.length - 2} más</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{formatDate(invoice.date)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{formatDate(invoice.dueDate)}</td>
                      <td className="py-3 px-4 text-right">
                        {(() => {
                          const invoiceDiscountTotal = (invoice.items ?? []).reduce(
                            (sum, it) => sum + parseFloat(it.discountAmount || '0'), 0
                          );
                          const invoicePromoTotal = (invoice.items ?? []).reduce(
                            (sum, it) => sum + parseFloat((it as any).promotionDiscountAmount || '0'), 0
                          );
                          return (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="font-mono font-medium text-gray-900 whitespace-nowrap">{formatCurrency(invoice.total)}</span>
                              {invoiceDiscountTotal > 0 && (
                                <span className="flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 whitespace-nowrap">
                                  <Tag className="h-2.5 w-2.5" />
                                  -{invoiceDiscountTotal.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
                                </span>
                              )}
                              {invoicePromoTotal > 0 && (
                                <span className="flex items-center gap-0.5 text-[10px] font-medium text-violet-600 whitespace-nowrap">
                                  ✦ -{invoicePromoTotal.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono text-green-600 whitespace-nowrap">{formatCurrency(invoice.totalPaid || '0')}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                        {invoice.lastPaymentDate ? formatDate(invoice.lastPaymentDate) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {invoice.pricingInfo && (invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID') ? (
                          <div className="flex flex-col items-end gap-0.5">
                            {invoice.pricingInfo.scenario === 'pronto' && (
                              <>
                                <span className="font-mono font-semibold text-green-700 whitespace-nowrap">
                                  {formatCurrency(invoice.pricingInfo.effectiveBalance)}
                                </span>
                                <span className="flex items-center gap-0.5 text-[10px] font-medium text-green-600 whitespace-nowrap">
                                  <Zap className="h-2.5 w-2.5" />
                                  Pronto Pago
                                </span>
                              </>
                            )}
                            {invoice.pricingInfo.scenario === 'normal' && (
                              <>
                                <span className="font-mono font-semibold text-orange-600 whitespace-nowrap">
                                  {formatCurrency(invoice.pricingInfo.effectiveBalance)}
                                </span>
                                <span className="flex items-center gap-0.5 text-[10px] font-medium text-orange-500 whitespace-nowrap">
                                  <Clock className="h-2.5 w-2.5" />
                                  Precio normal
                                </span>
                              </>
                            )}
                            {invoice.pricingInfo.scenario === 'mora' && (
                              <>
                                <span className="font-mono font-semibold text-red-700 whitespace-nowrap">
                                  {formatCurrency(invoice.pricingInfo.effectiveBalance)}
                                </span>
                                <span className="flex items-center gap-0.5 text-[10px] font-medium text-red-600 whitespace-nowrap">
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  ⚠ Mora
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className={`font-mono font-medium whitespace-nowrap ${parseFloat(invoice.pendingAmount || '0') > 0 ? 'text-orange-600' : 'text-gray-500'}`}>
                            {formatCurrency(invoice.pendingAmount || '0')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[invoice.status]}`}>
                            {statusLabels[invoice.status]}
                          </span>
                          {invoice.cfdiMetodoPago === 'PPD' && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 font-medium whitespace-nowrap">
                              PPD · saldo {formatCurrency(invoice.cfdiSaldoInsoluto || '0')}
                            </span>
                          )}
                          {invoice.status === 'CANCELLED' && invoice.replacedByInvoiceNumber && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700 font-medium whitespace-nowrap"
                              title="Este recibo fue cancelado y sustituido por otro"
                            >
                              Sustituida por {invoice.replacedByInvoiceNumber}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50">
              <p className="text-sm text-gray-500">
                {total > 0
                  ? `Mostrando ${from}–${to} de ${total.toLocaleString('es-MX')} registros`
                  : 'Sin registros'}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1 || isFetching}
                  title="Primera página"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isFetching}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 text-sm text-gray-700 font-medium min-w-[100px] text-center">
                  Página {page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || isFetching}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages || isFetching}
                  title="Última página"
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedInvoice && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false);
            setSelectedInvoice(null);
          }}
          invoice={selectedInvoice}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {cancelDialogInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-semibold">Cancelar factura {cancelDialogInvoice.invoiceNumber}</h2>
            <p className="text-sm text-gray-500">
              Elige si quieres cancelarla directo, o vincularla con otro recibo pendiente que la sustituye
              (por ejemplo, uno nuevo con un descuento aplicado). El recibo cancelado queda visible marcado
              como cancelado, nunca se borra.
            </p>
            <div>
              <Label className="text-xs">Recibo sustituto (opcional)</Label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={cancelLinkToId ?? ''}
                onChange={(e) => setCancelLinkToId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Cancelar directo, sin vincular</option>
                {linkableInvoices.map((inv: Invoice) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} — ${parseFloat(inv.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </option>
                ))}
              </select>
              {linkableInvoices.length === 0 && (
                <p className="text-[11px] text-gray-400 mt-1">Este alumno no tiene otros recibos pendientes para vincular.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setCancelDialogInvoice(null); setCancelLinkToId(null); }}>
                Cerrar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate({
                  invoiceId: cancelDialogInvoice.id,
                  linkToInvoiceId: cancelLinkToId ?? undefined,
                })}
              >
                {cancelMutation.isPending ? 'Procesando...' : cancelLinkToId ? 'Cancelar y vincular' : 'Cancelar directo'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
